import { Box, Paper, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Datepicker, GridContainer, GridItem, Select, ResyncDetails } from '../../../../components/shared';
import { parseValidDayjs } from '../../../../utils/dayjsDate';

const MonthlyReconciliationControlPanel = ({
  classes,
  selectedProductType,
  onProductTypeChange,
  productTypeOptions,
  productNamesById,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  postingDate,
  onPostingDateChange,
  areDateFieldsEnabled,
  minDate,
  startDateMax,
  endDateMin,
  maxSelectableDate,
  dateFormat,
  onApply,
  isApplyEnabled,
  resyncData = [],
}) => {
  const renderProductTypeOption = (option) => {
    const displayName = option?.displayName ?? option?.label ?? option?.text ?? '';

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box
          component="span"
          sx={{
            color: 'text.primary',
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
          }}
        >
          {displayName}
        </Box>
        {option?.dateRange ? (
          <Typography
            component="span"
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.72rem',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {option.dateRange}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const startDateValue = parseValidDayjs(startDate, { fallback: null });
  const endDateValue = parseValidDayjs(endDate, { fallback: null });
  const postingDateValue = parseValidDayjs(postingDate, { fallback: null });
  const minDateValue = parseValidDayjs(minDate, { fallback: undefined });
  const startDateMaxValue = parseValidDayjs(startDateMax, { fallback: undefined });
  const endDateMinValue = parseValidDayjs(endDateMin, { fallback: undefined });
  const maxSelectableDateValue = parseValidDayjs(maxSelectableDate, { fallback: undefined });

  return (
    <Paper elevation={0} className={classes.headerPanel}>
      <Box className={classes.headerDisclaimer}>
        <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
        <Typography variant="caption" component="span">
          Excluding Standalone Sub-Locations
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <ResyncDetails resyncData={resyncData} />
        </Box>
      </Box>
      <GridContainer spacing={2} alignItems="flex-end" className={classes.headerPanelContent}>
        <GridItem className={`${classes.formField} ${classes.productField}`}>
          <Typography variant="subtitle2" color="text.primary" className={classes.fieldLabel}>
            X3 Product Name
          </Typography>
          <Select
            value={selectedProductType}
            onChange={onProductTypeChange}
            options={productTypeOptions}
            size="small"
            displayEmpty
            renderOption={renderProductTypeOption}
            MenuProps={{
              PaperProps: {
                sx: {
                  minWidth: 520,
                },
              },
            }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Box component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}>
                    Select X3 Product Name
                  </Box>
                );
              }

              const selectedOption = productTypeOptions.find((option) => String(option?.value) === String(value));

              return (
                <Box component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}>
                  {selectedOption?.text ?? productNamesById[String(value)] ?? ''}
                </Box>
              );
            }}
          />
        </GridItem>

        <GridItem className={classes.formField}>
          <Typography
            variant="subtitle2"
            color={areDateFieldsEnabled ? 'text.primary' : 'text.disabled'}
            className={classes.fieldLabel}
          >
            Start Date
          </Typography>
          <Datepicker
            value={startDateValue}
            onChange={onStartDateChange}
            inputFormat={dateFormat}
            size="small"
            disabled={!selectedProductType}
            minDate={minDateValue}
            maxDate={startDateMaxValue}
            defaultCalendarMonth={minDateValue}
          />
        </GridItem>

        <GridItem className={classes.formField}>
          <Typography
            variant="subtitle2"
            color={areDateFieldsEnabled ? 'text.primary' : 'text.disabled'}
            className={classes.fieldLabel}
          >
            End Date
          </Typography>
          <Datepicker
            value={endDateValue}
            onChange={onEndDateChange}
            inputFormat={dateFormat}
            size="small"
            disabled={!selectedProductType}
            minDate={endDateMinValue}
            maxDate={maxSelectableDateValue}
            defaultCalendarMonth={endDateMinValue}
          />
        </GridItem>

        <GridItem className={classes.formField}>
          <Typography
            variant="subtitle2"
            color={areDateFieldsEnabled ? 'text.primary' : 'text.disabled'}
            className={classes.fieldLabel}
          >
            Posting Date
          </Typography>
          <Datepicker
            value={postingDateValue}
            onChange={onPostingDateChange}
            inputFormat={dateFormat}
            size="small"
            disabled={!selectedProductType}
          />
        </GridItem>

        <GridItem xs={12} sm={6} md="auto" className={classes.applyButtonWrap}>
          <Button
            variant="contained"
            color="primary"
            onClick={onApply}
            className={classes.applyButton}
            sx={{ minWidth: { xs: '100%', sm: 140 } }}
            disabled={!isApplyEnabled}
          >
            Apply
          </Button>
        </GridItem>
      </GridContainer>
    </Paper>
  );
};

export default MonthlyReconciliationControlPanel;
